import { inject, nextTick, onBeforeUnmount, onMounted, watch, type Ref } from 'vue';

import { MF_NAME } from '@/shared/config/env';

import type { RouteLocationNormalizedLoaded } from 'vue-router';

const parseAnchorId = (route: RouteLocationNormalizedLoaded): string | null => {
  const hash = window.location.hash || route.hash;
  if (!hash || hash === '#') {
    return null;
  }
  let id = hash.slice(1);
  try {
    id = decodeURIComponent(id);
  } catch {
    /* raw */
  }
  return id || null;
};

const findAnchor = (root: ShadowRoot | null, anchorId: string): HTMLElement | null => {
  const fromShadow = root?.getElementById(anchorId);
  if (fromShadow instanceof HTMLElement) {
    return fromShadow;
  }
  const fromDocument = document.getElementById(anchorId);
  return fromDocument instanceof HTMLElement ? fromDocument : null;
};

const waitForNode = (pick: () => HTMLElement | null, deadline: number): Promise<HTMLElement | null> => {
  return new Promise((resolve) => {
    const step = () => {
      const hit = pick();
      if (hit) {
        resolve(hit);
      } else if (performance.now() >= deadline) {
        resolve(null);
      } else {
        requestAnimationFrame(step);
      }
    };
    step();
  });
};

/** `contentId` — идентификатор загруженного контента: пока он пустой, якорей в разметке ещё нет. */
export const useArticleHashScroll = (
  route: RouteLocationNormalizedLoaded,
  contentId: Ref<number | null | undefined>,
) => {
  const shadowRoot = inject<ShadowRoot | null>('shadowRoot', null);
  const timeouts: ReturnType<typeof setTimeout>[] = [];
  let active = true;

  const clear = (): void => {
    timeouts.forEach(clearTimeout);
    timeouts.length = 0;
  };

  const go = async (): Promise<void> => {
    const anchorId = parseAnchorId(route);
    if (!anchorId || contentId.value == null) {
      return;
    }
    const root = shadowRoot ?? document.querySelector(MF_NAME)?.shadowRoot ?? null;
    const pick = () => findAnchor(root, anchorId);

    clear();
    await nextTick();

    const node = await waitForNode(pick, performance.now() + 4000);
    if (!active || !node) {
      return;
    }

    (() => pick()?.scrollIntoView({ behavior: 'auto', block: 'start', inline: 'nearest' }))();

    timeouts.push(
      setTimeout(() => pick()?.scrollIntoView({ behavior: 'auto', block: 'start', inline: 'nearest' }), 400),
    );
  };

  const kick = (): void => {
    go().catch(() => {});
  };

  const stopWatch = watch(() => [route.hash, route.fullPath, contentId.value] as const, kick, {
    flush: 'post',
  });
  onMounted(() => {
    window.addEventListener('hashchange', kick);
  });
  onBeforeUnmount(() => {
    active = false;
    stopWatch();
    clear();
    window.removeEventListener('hashchange', kick);
  });
};
